import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { POST_UNLOCK_ROUTE } from '@constants/routes'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { useToast } from '@shared/ui/useToast'
import { useTranslation } from '@/i18n'
import { sessionStore } from '../services/sessionStore'
import { unlockWithMasterPassword } from '../services/unlockFlow'
import { setupPin } from '../services/pinVaultService'

/**
 * Lock screen から「マスターパスワードで解錠」+「PIN を有効化」を 1 ステップで行う
 * モーダル (T-C3-PIN-onboarding、ookey-browser parity)。
 *
 * フロー:
 *   1. master password + PIN + PIN 確認の 3 input を受け取る
 *   2. submit: unlockWithMasterPassword(pw) → SK を sessionStore に展開
 *   3. setupPin(pin): SK を PIN で wrap して IndexedDB に保存
 *   4. 成功時は POST_UNLOCK_ROUTE へ遷移 (= 通常の unlock と同じ着地点)
 *
 * エラーハンドル:
 *   - master password 誤り: form 内に inline エラー、再入力可能
 *   - PIN setup 失敗: vault は既に unlocked になっているため、toast で警告を出し
 *     vault に遷移する (ユーザーは Settings → Security から再設定可能)。
 */

const PIN_LENGTH_MIN = 4
const PIN_LENGTH_MAX = 6

type ErrorKind = 'wrong-password' | 'mismatch' | 'length' | 'setup-failed' | null

export interface UnlockPinSetupModalProps {
  onClose: () => void
}

export const UnlockPinSetupModal = ({ onClose }: UnlockPinSetupModalProps): JSX.Element => {
  const { t } = useTranslation()
  const toast = useToast()
  const passwordId = useId()
  const pinId = useId()
  const pinConfirmId = useId()
  const submittingRef = useRef(false)

  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [errorKind, setErrorKind] = useState<ErrorKind>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Escape で閉じる + bg scroll lock
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !submittingRef.current) onClose()
    }

    document.addEventListener('keydown', onKey)

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  const validatePin = (value: string): ErrorKind => {
    if (!/^\d{4,6}$/u.test(value)) return 'length'

    return null
  }

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()
    if (submittingRef.current) return

    const data = new FormData(ev.currentTarget)
    const password = String(data.get('password') ?? '')

    const lengthError = validatePin(pin)
    if (lengthError) {
      setErrorKind(lengthError)

      return
    }
    if (pin !== pinConfirm) {
      setErrorKind('mismatch')

      return
    }

    const material = sessionStore.getMaterial()
    if (material === null) {
      // LOCKED 中で material が無いのは想定外 — 安全側にモーダルを閉じる
      onClose()

      return
    }

    submittingRef.current = true
    setIsSubmitting(true)
    setErrorKind(null)

    let sk: Uint8Array
    try {
      sk = await unlockWithMasterPassword(password, material)
    } catch {
      setErrorKind('wrong-password')
      submittingRef.current = false
      setIsSubmitting(false)

      return
    }

    sessionStore.unlock(sk)

    try {
      await setupPin(pin)
    } catch {
      // unlock は成功しているので、PIN 設定は後で再試行できる旨を伝えつつ vault へ。
      toast.show({ tone: 'warn', titleKey: 'auth.lock.pin_setup_setup_error' })
      window.location.hash = POST_UNLOCK_ROUTE

      return
    }

    window.location.hash = POST_UNLOCK_ROUTE
  }

  const errorMessage = (): string | null => {
    if (errorKind === 'wrong-password') return t('auth.lock.pin_setup_unlock_error')
    if (errorKind === 'mismatch') return t('auth.lock.pin_setup_pin_mismatch')
    if (errorKind === 'length') return t('auth.lock.pin_setup_pin_length')

    return null
  }

  const canSubmit =
    pin.length >= PIN_LENGTH_MIN &&
    pin.length <= PIN_LENGTH_MAX &&
    pinConfirm.length >= PIN_LENGTH_MIN &&
    !isSubmitting

  return (
    <div
      className="vault-delete-overlay"
      role="presentation"
      onClick={ev => {
        if (ev.target === ev.currentTarget && !submittingRef.current) onClose()
      }}
    >
      <div
        className="vault-delete-dialog pin-setup-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pin-setup-title"
      >
        <h3 id="pin-setup-title">{t('auth.lock.pin_setup_modal_title')}</h3>
        <p>{t('auth.lock.pin_setup_modal_subtitle')}</p>

        <form onSubmit={ev => void onSubmit(ev)} noValidate>
          <div className="field">
            <label className="field-label" htmlFor={passwordId}>
              {t('auth.lock.pin_setup_master_password')}
            </label>
            <PasswordInput
              id={passwordId}
              name="password"
              autoComplete="current-password"
              autoFocus
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor={pinId}>
              {t('auth.lock.pin_setup_pin')}
            </label>
            <PasswordInput
              id={pinId}
              name="pin"
              autoComplete="off"
              inputMode="numeric"
              minLength={PIN_LENGTH_MIN}
              maxLength={PIN_LENGTH_MAX}
              required
              disabled={isSubmitting}
              value={pin}
              onChange={ev => setPin(ev.target.value.replace(/\D/gu, '').slice(0, PIN_LENGTH_MAX))}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor={pinConfirmId}>
              {t('auth.lock.pin_setup_pin_confirm')}
            </label>
            <PasswordInput
              id={pinConfirmId}
              name="pinConfirm"
              autoComplete="off"
              inputMode="numeric"
              minLength={PIN_LENGTH_MIN}
              maxLength={PIN_LENGTH_MAX}
              required
              disabled={isSubmitting}
              value={pinConfirm}
              onChange={ev =>
                setPinConfirm(ev.target.value.replace(/\D/gu, '').slice(0, PIN_LENGTH_MAX))
              }
            />
          </div>

          {errorMessage() !== null && (
            <p className="field-error" role="alert">
              {errorMessage()}
            </p>
          )}

          <div className="vault-delete-actions">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={isSubmitting}
              onClick={onClose}
            >
              {t('auth.lock.pin_setup_cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {isSubmitting
                ? t('auth.lock.pin_setup_submitting')
                : t('auth.lock.pin_setup_submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
